import type { AxiosInstance } from 'axios';
import type { DocumentSnapshot } from '../types/snapshot';
import type { UpdateTemplateRequest, TemplatePlaceholder, TemplateSigningField, PlaceholderScanResult } from '../types/template';
import type { DocumentReview, ReviewActivity } from '../types/review';
import type { PaginatedResponse, PaginationParams } from '../types/common';

export interface CrossOrgSearchRequest {
  filters?: Record<string, unknown>;
  search?: string;
}

export function createCrossOrgSnapshotService(client: AxiosInstance) {
  const base = '/v1/cross-org/snapshots';

  return {
    // ── Search & CRUD ──
    search: (body?: CrossOrgSearchRequest, params?: PaginationParams) =>
      client.post<PaginatedResponse<DocumentSnapshot>>(
        `${base}/search`, body ?? {}, { params },
      ).then(r => r.data),

    getById: (id: string) =>
      client.get<DocumentSnapshot>(`${base}/${id}`).then(r => r.data),

    update: (id: string, data: UpdateTemplateRequest) =>
      client.put<DocumentSnapshot>(`${base}/${id}`, data).then(r => r.data),

    // ── Lifecycle ──
    approve: (id: string) =>
      client.post<DocumentSnapshot>(`${base}/${id}/approve`).then(r => r.data),

    deactivate: (id: string, reason: string) =>
      client.post<DocumentSnapshot>(`${base}/${id}/deactivate`, { reason }).then(r => r.data),

    // ── Placeholders ──
    // OnlyOffice forcesave — the shared /v1/onlyoffice/forcesave route persists
    // an in-memory document session to its backing file via the document-server
    // callback. Callers MUST forceSave + wait before scanPlaceholders, otherwise
    // the scan reads stale storage and misses recent edits.
    forceSave: (docKey: string) =>
      client.post(`/v1/onlyoffice/forcesave`, null, { params: { key: docKey } }),

    scanPlaceholders: (id: string) =>
      client.post<PlaceholderScanResult>(`${base}/${id}/scan-placeholders`).then(r => r.data),

    getDataPlaceholders: (id: string) =>
      client.get<TemplatePlaceholder[]>(`${base}/${id}/data-placeholders`).then(r => r.data),

    upsertDataPlaceholders: (id: string, placeholders: TemplatePlaceholder[]) =>
      client.post<TemplatePlaceholder[]>(`${base}/${id}/data-placeholders`, placeholders).then(r => r.data),

    // ── Signing fields ──
    getSigningFields: (id: string) =>
      client.get<TemplateSigningField[]>(`${base}/${id}/signing-fields`).then(r => r.data),

    upsertSigningFields: (id: string, fields: TemplateSigningField[]) =>
      client.post<TemplateSigningField[]>(`${base}/${id}/signing-fields`, fields).then(r => r.data),

    deleteSigningField: (id: string, fieldId: string) =>
      client.delete(`${base}/${id}/signing-fields/${fieldId}`),

    // ── Preview & PDF generation ──
    preview: (id: string) =>
      client.get<Blob>(`${base}/${id}/preview`, { responseType: 'blob' }),

    generatePdf: (id: string, values: Record<string, string>) =>
      client.post<Blob>(`${base}/${id}/generate-pdf`, values, { responseType: 'blob' }),

    // ── OnlyOffice editor config (raw, NOT ApiResponse wrapped) ──
    getEditorConfig: (id: string, mode?: string) =>
      client.get<Record<string, unknown>>(`${base}/${id}/editor-config`, {
        params: mode ? { mode } : undefined,
      }).then(r => r.data),

    // ── Review management ──
    getReview: (id: string) =>
      client.get<DocumentReview>(`${base}/${id}/review`).then(r => r.data),

    getReviewActivities: (id: string) =>
      client.get<ReviewActivity[]>(`${base}/${id}/review/activities`).then(r => r.data),

    addReviewer: (id: string, data: { name: string; email: string }) =>
      client.post<DocumentReview>(`${base}/${id}/review/reviewers`, data).then(r => r.data),

    removeReviewer: (id: string, reviewerId: string) =>
      client.delete(`${base}/${id}/review/reviewers/${reviewerId}`),

    markAuthorComplete: (id: string) =>
      client.post(`${base}/${id}/review/mark-author-complete`),

    markReviewerComplete: (id: string, reviewerId: string) =>
      client.post(`${base}/${id}/review/reviewers/${reviewerId}/mark-complete`),

    regenerateToken: (id: string, reviewerId: string) =>
      client.post(`${base}/${id}/review/reviewers/${reviewerId}/regenerate-token`).then(r => r.data),
  };
}
